import { Modal, useModals, ModalProps, Button, Stack, Textarea } from "@skailer-com/uikit";
import { useApplicantCVUpdate } from "@/queries/recruit/use-applicants.query";
import analyticsService, { analyticEvents, analyticProperties } from "@/services/analytics-service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface ApplicantCVTextEditModalProps extends ModalProps {
  applicantId: number;
  applicantCVText: string;
  applicantCVId: number;
  onSave?: (updatedCVText: string) => void;
}

export const ApplicantCVTextEditModal = ({
  applicantId,
  applicantCVText,
  applicantCVId,
  onSave,
  ...props
}: ApplicantCVTextEditModalProps) => {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = props;

  const schema = z.object({
    cv_text: z.string().min(1, t("errors:field_required")),
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      cv_text: applicantCVText,
    },
  });

  const applicantCVUpdate = useApplicantCVUpdate();

  const onSubmit = handleSubmit(async (data) => {
    if (!data.cv_text) return;

    try {
      await applicantCVUpdate.mutateAsync({
        id: applicantId,
        payload: { cv_text: data.cv_text, cv_id: applicantCVId },
      });
      analyticsService.trackEvent(analyticEvents.applicants.cvTextEdited, {
        [analyticProperties.id]: applicantId,
      });
      onSave?.(data.cv_text);
      close(id);
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      variant="side"
      title={t("ats:edit_cv_text")}
      layout="stretch"
      withCloser={true}
      actions={
        <Stack gap="sm">
          <Button
            size="lg"
            className="w-full"
            type="submit"
            isLoading={applicantCVUpdate.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Textarea
          {...register("cv_text")}
          label={t("ats:cv_text")}
          placeholder={t("ats:cv_text_enter") + "..."}
          error={errors.cv_text?.message}
          rows={15}
        />
      </form>
    </Modal>
  );
};
